<template>
  <div class="stream">
    <v-snackbar
      v-model="isClosedStream"
      color="error"
      bottom
      :timeout="1000000"
    >
      配信を終了しました。
    </v-snackbar>

    <Confirm ref="confirm" />
    <Share ref="share" />

    <v-dialog
      v-model="selectDialog"
      max-width="500"
    >
      <v-card outlined>
        <v-card-title>
          <span>音源ダウンロード</span>
          <v-spacer />
          <v-btn
            fab
            text
            x-small
            @click="selectDialog = false"
          >
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="ma-0">
            {{ soundId && $_musicalInstruments().info[soundId].text }}
            ({{ soundId && $_musicalInstruments().info[soundId].size }})のダウンロードを開始します。<br>
            (ページを移動してもダウンロードは継続されます。)
          </p>
          <v-row>
            <v-spacer />
            <v-col class="col-12 col-sm-6 pb-0">
              <v-btn
                block
                depressed
                color="secondary"
                @click="getSounds()"
              >
                確認
              </v-btn>
            </v-col>
            <v-spacer />
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-row>
      <v-col class="pa-0 pb-3">
        <v-card outlined>
          <v-card-text class="pa-2 pa-sm-3">
            <Keyboard
              :loaded-sound-size="useSounds ? loadedSoundSize : 88"
              :action-virtual-key="actionVirtualKey"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="col-12 col-sm-6 pa-0">
        <v-col class="pa-0 pb-3">
          <v-card
            outlined
            :loading="isLoading.savePostDetails || isLoading.getPostDetails"
          >
            <v-card-title>
              <span>投稿詳細</span>
              <v-tooltip
                right
                max-width="350"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    class="ml-1"
                    v-on="on"
                  >
                    far fa-question-circle
                  </v-icon>
                </template>
                <p class="mb-0">
                  投稿の詳細です。ライブ配信の場合は、これが設定された時点でユーザーが入室することができます。
                  配信の再開の場合は、前のデータがそのまま表示されます。
                </p>
              </v-tooltip>
              <v-spacer />
              <v-btn
                icon
                color="secondary"
                :disabled="!postId"
                class="mr-3"
                @click="sharePost"
              >
                <div>
                  <v-icon small>
                    fas fa-share-square
                  </v-icon>
                </div>
              </v-btn>
              <v-btn
                v-if="!$route.params.postId"
                depressed
                color="primary"
                :disabled="!postDetailsFormValid || isLoading.savePostDetails || canStream"
                @click="savePostDetails"
              >
                設定
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-form
                ref="postDetailsForm"
                v-model="postDetailsFormValid"
              >
                <v-text-field
                  v-model="postDetails.title"
                  counter="50"
                  outlined
                  dense
                  label="タイトル"
                  :rules="postDetailsRules.title"
                  :disabled="canStream || !!$route.params.postId"
                  required
                />
                <v-textarea
                  v-model="postDetails.explanation"
                  counter="200"
                  rows="3"
                  auto-grow
                  outlined
                  dense
                  label="説明"
                  :rules="postDetailsRules.explanation"
                  :disabled="canStream || !!$route.params.postId"
                  required
                />
                <v-select
                  v-model="postDetails.mode"
                  :items="modes"
                  outlined
                  dense
                  label="形式"
                  hide-details
                  :rules="postDetailsRules.mode"
                  :disabled="canStream || !!$route.params.postId"
                  required
                />
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col class="pa-0 pb-3">
          <v-card outlined>
            <v-card-title>
              <span>機器設定</span>
              <v-tooltip
                right
                max-width="350"
              >
                <template v-slot:activator="{ on }">
                  <v-icon
                    class="ml-1"
                    v-on="on"
                  >
                    far fa-question-circle
                  </v-icon>
                </template>
                <p class="mb-0">
                  配信に使用するMIDIデバイスを選択します。仮想ピアノの情報も同時に配信されます。
                  ブラウザで音を鳴らしたいときは、スイッチをオンにし楽器を選択します。
                </p>
              </v-tooltip>
            </v-card-title>
            <v-card-text class="pb-2">
              <v-row>
                <v-col class="col-12 col-lg-6">
                  <v-select
                    v-model="selectedMidiInput"
                    :items="midiInputDevices"
                    :success="!!selectedMidiInput"
                    label="MIDIデバイス (INPUT)"
                    hide-details
                    outlined
                    dense
                    append-icon="fa-sync"
                    class="dense-select"
                    @click:append="getMidiDevices"
                  />
                </v-col>
                <v-col class="col-12 col-lg-6">
                  <v-row class="ma-0">
                    <v-col class="pa-0">
                      <v-select
                        v-model="soundId"
                        :items="$_musicalInstruments().selects"
                        :success="soundId && $_musicalInstruments().info[soundId].isLoaded"
                        :disabled="!useSounds || isLoading.sounds"
                        :loading="isLoading.sounds"
                        label="楽器"
                        hide-details
                        outlined
                        dense
                        class="dense-select"
                      >
                        <template v-slot:progress>
                          <v-progress-linear
                            :value="loadedSoundSize*100/88"
                            color="success"
                            absolute
                          />
                        </template>
                        <v-icon
                          v-if="soundId && !$_musicalInstruments().info[soundId].isLoaded"
                          slot="append"
                          :disabled="isLoading.sounds"
                          @click="selectDialog = true"
                        >
                          fa-download
                        </v-icon>
                      </v-select>
                    </v-col>
                  </v-row>
                  <v-row class="ma-0">
                    <v-col class="px-0">
                      <v-slider
                        v-model="volumeRatio"
                        min="0"
                        max="100"
                        thumb-label
                        hide-details
                        :disabled="!useSounds"
                        append-icon="fa-volume-up"
                        prepend-icon="fa-volume-down"
                        class="volume-slider"
                        @click:append="volumeRatio += 10"
                        @click:prepend="volumeRatio -= 10"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="ma-0">
                    <v-col class="pa-0 pt-0">
                      <v-switch
                        v-model="useSounds"
                        label="ブラウザで音を鳴らす"
                        hide-details
                        class="mt-1"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-col>
      <v-col class="col-12 col-sm-6 pa-0 pl-sm-3">
        <v-row class="ma-0">
          <v-col class="col-12 col-xl-6 pa-0 pb-3">
            <v-card outlined>
              <v-card-title>
                <span>配信設定</span>
                <v-tooltip
                  right
                  max-width="350"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      class="ml-1"
                      v-on="on"
                    >
                      far fa-question-circle
                    </v-icon>
                  </template>
                  <p class="mb-0">
                    右の時間は現在の配信時間ではなく、最初の音から最後の音までの時間です。
                    配信を再開した場合は合計時間ではなく、再開してからの最初の音から最後の音までの時間です。
                  </p>
                </v-tooltip>
                <v-spacer />
                <span>{{ $_convertPlayTime(nowPlayTime) }}</span>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col class="col-12 col-sm-6">
                    <v-btn
                      :color="sendIntervalId ? 'warning' : 'success'"
                      block
                      depressed
                      :disabled="!canStream || isClosedStream"
                      @click="updateSendInterval"
                    >
                      {{ sendIntervalId ? '配信停止' : '配信開始' }}
                    </v-btn>
                  </v-col>
                  <v-col class="col-12 col-sm-6">
                    <v-btn
                      color="error"
                      block
                      depressed
                      :disabled="!streamStartTime || isClosedStream"
                      @click="closeStream()"
                    >
                      配信終了
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col class="col-12 col-xl-6 pa-0 pl-xl-3">
            <v-card outlined>
              <v-card-title class="pb-2">
                <span>遅延情報</span>
                <v-tooltip
                  right
                  max-width="350"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      class="ml-1"
                      v-on="on"
                    >
                      far fa-question-circle
                    </v-icon>
                  </template>
                  <p>
                    「標準時刻との差」は配信を開始した時点で取得されます。
                    この値の大きさは配信には影響ありません。ただし、時間を取得できなかった場合は、
                    受信側で遅延の処理に問題が発生する可能性があります。
                  </p>
                  <p class="mb-0">
                    「平均遅延時間」はサーバに送信したデータが返ってくるのにかかった時間の平均です。
                    受信するユーザーの場所にもよりますが遅延時間の目安になります。
                  </p>
                </v-tooltip>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col>
                    <v-text-field
                      label="標準時刻との差"
                      suffix="ms"
                      readonly
                      :disabled="!canStream"
                      hide-details
                      type="number"
                      :value="Math.floor(-ntpTimeDiff*10)/10"
                    />
                  </v-col>
                  <v-col>
                    <v-text-field
                      label="平均遅延時間"
                      suffix="ms"
                      readonly
                      :disabled="!sendIntervalId"
                      hide-details
                      type="number"
                      :value="Math.floor(avrDelay*10)/10"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss">
.stream {
  > * {
    margin: auto;
  }
  .volume-slider {
    .v-icon::after {
      background-color: initial;
    }
  }
  .dense-select .v-icon {
    font-size: 17px;
    padding: 3px;
  }
}
</style>

<script>
import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/firestore';

export default {
  data: () => ({
    ntpTimeDiff: 0,
    midi: null,
    noMidiDevice: true,
    selectedMidiInput: null,
    isLoading: {
      sounds: false,
      getPostDetails: false,
      savePostDetails: false
    },
    useSounds: false,
    soundId: null,
    loadedSoundSize: 0,
    noSoundFile: false,
    notes: new Map(),
    maxVolume: 0.3,
    volumeRatio: 30,
    isDamper: false,
    damperNotes: [],
    selectDialog: false,
    delay: 0,
    countGetMessage: 0,
    avrDelay: 0,
    sendIntervalId: null,
    intervalTime: 50,
    stackStartTime: null,
    stackedMidiSignals: [],
    archiveArray: [],
    streamStartTime: null,
    isClosedStream: false,
    postDetailsFormValid: true,
    modes: [
      { text: 'ライブ配信', value: 'stream' } // , { text: 'アップロード', value: 'upload' }
    ],
    postDetailsRules: {
      title: [
        v => !!v || '必須項目です。',
        v => v.length <= 50 || '50文字以内で入力してください。'
      ],
      explanation: [
        v => v.length <= 200 || '200文字以内で入力してください。'
      ],
      mode: [v => !!v || '必須項目です。']
    },
    postDetails: {
      title: '',
      explanation: '',
      mode: 'stream'
    },
    postId: null,
    canStream: false
  }),
  computed: {
    midiInputDevices() {
      const inputs = [{
        text: '未選択', value: null
      }];
      if (this.midi && !this.noMidiDevice) {
        inputs.push(...this.midi.inputs.map(obj => ({ text: obj.name, value: obj })));
      }
      return inputs;
    },
    nowPlayTime() {
      return (this.archiveArray.slice(-1)[0] || {}).time - (this.archiveArray[0] || {}).time;
    }
  },
  watch: {
    $route() {
      this.$router.push({ name: 'home' });
    },
    selectedMidiInput(newVal, oldVal) {
      if (oldVal) {
        oldVal.onmidimessage = () => {};
      }
      if (newVal) {
        newVal.onmidimessage = e => {
          if (e.data.length !== 1) {
            this.stackMidiSignal(this.notes, e.data, this.volumeRatio);
          }
        };
      }
    },
    soundId(val) {
      const selectedSoundInfo = this.$_musicalInstruments().info[val];
      this.loadedSoundSize = selectedSoundInfo.sounds.size;
      if (selectedSoundInfo.isLoaded) {
        this.loadedSoundSize = 0;
        for (let noteId = 21; noteId < 88 + 21; noteId++) {
          this.notes.set(noteId, {
            origSound: selectedSoundInfo.sounds.get(noteId),
            playNotes: []
          });
          this.loadedSoundSize++;
        }
        this.$_pushNotice('音源の読み込みが完了しました。', 'success');
      } else {
        this.selectDialog = true;
      }
    }
  },
  async mounted() {
    if (!navigator.requestMIDIAccess) {
      await this.$refs.confirm.open('このブラウザでの配信はサポートされていません。\n前のページに戻ります。');
      this.$router.go(-1);
      return;
    }
    firebase.database().goOnline();
    await this.getMidiDevices();
    if (this.$route.params.postId) {
      this.isLoading.getPostDetails = true;
      await this.getPostDetails();
      if (this.postDetails.playingTime || this.postDetails.notMyPost) {
        this.$_pushNotice('この投稿の配信を再開することはできません。', 'error');
        return;
      }
      this.postId = this.$route.params.postId;
      await firebase.database().ref(`/users/${this.$_myUserData.uid}/posts/${this.postId}/isOffline`).remove();
      firebase.database().ref(`/users/${this.$_myUserData.uid}/posts/${this.postId}/isOffline`).onDisconnect().set(true);
      this.$store.dispatch('changeFirebaseState', true);
      window.addEventListener('beforeunload', this.$_beforeunloadHandler);
      this.canStream = true;
      this.$_pushNotice('ライブ配信を再開しました。', 'success');
    }
  },
  destroyed() {
    if (this.selectedMidiInput) {
      this.selectedMidiInput.onmidimessage = () => {};
    }
    if (this.postId && !this.isClosedStream) {
      this.closeStream(true);
    }
  },
  methods: {
    async getPostDetails() {
      this.isLoading.getPostDetails = true;
      try {
        const postRef = await firebase.firestore().collection('users').doc(this.$_myUserData.uid).collection('posts').doc(this.$route.params.postId).get();
        if (!postRef.exists) {
          throw new Error();
        }
        const postDetails = postRef.data();
        if (postDetails.uid === this.$_myUserData.uid) {
          this.postDetails = postDetails;
        } else {
          throw new Error();
        }
      } catch (err) {
        console.error(err);
        this.postDetails.notMyPost = true;
      } finally {
        this.isLoading.getPostDetails = false;
      }
    },
    async savePostDetails() {
      this.isLoading.savePostDetails = true;
      if (!this.$refs.postDetailsForm.validate()) {
        return;
      }
      try {
        const { uid, displayName, photoURL } = this.$_myUserData;
        const postDetails = { uid, displayName, photoURL, ...this.postDetails };
        const postsRef = firebase.firestore().collection('users').doc(this.$_myUserData.uid).collection('posts');
        if (this.postId) {
          await postsRef.doc(this.postId).set({
            ...postDetails,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp()
          }, { merge: true });
        } else {
          const docRef = postsRef.doc();
          this.postId = docRef.id;
          await docRef.set({
            postId: this.postId,
            ...postDetails,
            favCount: 0,
            playCount: 0,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp()
          });
        }
        this.canStream = true;
        firebase.database().ref(`/users/${this.$_myUserData.uid}/posts/${this.postId}/isOffline`).onDisconnect().set(true);
        this.$store.dispatch('changeFirebaseState', true);
        window.addEventListener('beforeunload', this.$_beforeunloadHandler);
        this.$_pushNotice('投稿詳細の設定が完了しました。', 'success');
      } catch (err) {
        console.error(err);
        this.$_pushNotice('投稿詳細の設定時にエラーが発生しました。', 'error');
      } finally {
        await this.$_sleep(500);
        this.isLoading.savePostDetails = false;
      }
    },
    async getMidiDevices() {
      this.midi = await new Promise(resolve => {
        if (!navigator.requestMIDIAccess) {
          resolve(undefined);
        }
        navigator.requestMIDIAccess()
          .then(access => {
            const inputs = [];
            const outputs = [];
            const it = access.inputs.values();
            const ot = access.outputs.values();
            for (let o = it.next(); !o.done; o = it.next()) {
              inputs.push(o.value);
            }
            for (let o = ot.next(); !o.done; o = ot.next()) {
              outputs.push(o.value);
            }
            resolve({ access, inputs, outputs });
          }, err => {
            console.error(err);
            resolve(undefined);
          });
      });
      console.log('midi', this.midi);
      if (!this.midi || !this.midi.inputs.length || !this.midi.outputs.length) {
        this.noMidiDevice = true;
        this.$_pushNotice('MIDIデバイスを取得できませんでした。', 'error');
      } else {
        this.noMidiDevice = false;
        this.selectedMidiInput = null;
        this.$_pushNotice('MIDIデバイスを取得しました。', 'success');
      }
      console.log('MIDI Devices:', this.midi);
    },
    async getSounds() {
      this.isLoading.sounds = true;
      this.selectDialog = false;
      this.loadedSoundSize = 0;
      const selectedSoundInfo = this.$_musicalInstruments().info[this.soundId];
      let beforeAbleNoteId = 21;
      for (let noteId = 21; !this.noSoundFile && noteId < 88 + 21; noteId++) {
        if (!selectedSoundInfo.sounds.has(noteId)) {
          const url = `/sounds/${this.soundId}/${noteId}.${selectedSoundInfo.extension}`;
          await new Promise((resolve, reject) => {
            const origSound = new Howl({ src: [url] });
            origSound.once('load', () => {
              this.$store.dispatch('setSoundData', {
                soundId: this.soundId,
                noteId,
                origSound
              });
              this.notes.set(noteId, { origSound, playNotes: [] });
              beforeAbleNoteId = noteId;
              resolve();
            });
            origSound.on('loaderror', () => {
              if (!beforeAbleNoteId) {
                reject();
              }
              this.$store.dispatch('setSoundData', {
                soundId: this.soundId,
                noteId,
                origSound: beforeAbleNoteId
              });
              this.notes.set(noteId, {
                origSound: beforeAbleNoteId,
                playNotes: []
              });
              resolve();
            });
          })
            .catch(() => {
              this.noSoundFile = true;
            });
        } else {
          this.notes.set(noteId, {
            origSound: selectedSoundInfo.sounds.get(noteId),
            playNotes: []
          });
        }
        this.loadedSoundSize++;
      }
      if (this.noSoundFile) {
        this.$_pushNotice('音源のダウンロードに失敗しました。', 'error');
        console.error('NO SOUND FILES');
      } else {
        this.$_pushNotice('音源のダウンロードが完了しました。', 'success');
      }
      this.isLoading.sounds = false;
    },
    playMidi(notes, data, volumeRatio) {
      const note = notes.get(data[1]);
      if (!note && this.useSounds) {
        return;
      }
      const keyDom = document.querySelectorAll('.keyboard > div')[data[1] - 21];
      if (keyDom && data[0] === 144) {
        keyDom.classList.add('key-active');
      } else if (keyDom && data[0] === 128) {
        keyDom.classList.remove('key-active');
      }
      if (!this.useSounds) {
        return;
      }
      if (data[0] === 144) {
        const volume = this.maxVolume*(Math.min(data[2], 127)/127)*(volumeRatio/100);
        if (typeof note.origSound !== 'number') {
          const sound = note.origSound.play();
          note.origSound.volume(volume, sound);
          note.playNotes.push({ sound, volume });
        } else {
          const beforeAbleSound = notes.get(note.origSound).origSound;
          const sound = beforeAbleSound.play();
          beforeAbleSound.rate(2**((data[1] - note.origSound)/12), sound);
          beforeAbleSound.volume(volume, sound);
          note.playNotes.push({ sound, volume });
        }
      } else if (data[0] === 128) {
        if (this.isDamper) {
          this.damperNotes.push(data[1]);
        } else {
          for (const playNote of note.playNotes) {
            if (typeof note.origSound !== 'number') {
              note.origSound.fade(playNote.volume, 0, 300, playNote.sound);
            } else {
              notes.get(note.origSound).origSound.fade(playNote.volume, 0, 300, playNote.sound);
            }
          }
          note.playNotes = [];
        }
      } else if (data[0] === 176 && data[1] === 64) {
        if (data[2] > 0) {
          this.isDamper = true;
        } else {
          this.isDamper = false;
          for (const noteId of this.damperNotes) {
            const note = notes.get(noteId);
            for (const playNote of note.playNotes) {
              if (typeof note.origSound !== 'number') {
                note.origSound.fade(playNote.volume, 0, 300, playNote.sound);
              } else {
                notes.get(note.origSound).origSound.fade(playNote.volume, 0, 300, playNote.sound);
              }
            }
            note.playNotes = [];
          }
          this.damperNotes = [];
        }
      }
    },
    actionVirtualKey(noteId, isClick) {
      if (!this.useSounds) {
        return;
      }
      const data = [isClick ? 144 : 128, noteId, 64];
      const note = this.notes.get(noteId);
      if (!isClick && (!note || !note.playNotes.length)) {
        return;
      }
      this.stackMidiSignal(this.notes, data, this.volumeRatio);
    },
    stackMidiSignal(notes, data, volumeRatio) {
      if (this.sendIntervalId) {
        if (!this.stackStartTime) {
          this.stackStartTime = this.$_getNowTime(this.ntpTimeDiff);
        }
        this.stackedMidiSignals.push({
          data,
          delay: this.$_getNowTime(this.ntpTimeDiff) - this.stackStartTime
        });
      }
      this.playMidi(notes, data, volumeRatio);
    },
    async sendMidiSignals() {
      if (!this.stackedMidiSignals.length || this.isClosedStream) {
        return;
      }
      this.stackStartTime = null;
      const stackedMidiSignals = this.stackedMidiSignals;
      this.stackedMidiSignals = [];
      const sendTime = this.$_getNowTime(this.ntpTimeDiff);
      this.archiveArray.push({
        signals: stackedMidiSignals,
        time: sendTime
      });
      await firebase.database().ref(`/users/${this.$_myUserData.uid}/posts/${this.postId}/midi`).push({
        signals: stackedMidiSignals,
        time: sendTime
      })
        .then(() => {
          this.delay = this.$_getNowTime(this.ntpTimeDiff) - sendTime;
          this.avrDelay = (this.avrDelay*this.countGetMessage + this.delay)/++this.countGetMessage;
        });
    },
    async sendMessage(text, type, data = {}) {
      if (!this.postId || this.isClosedStream) {
        return;
      }
      const sendTime = this.$_getNowTime(this.ntpTimeDiff);
      await firebase.database().ref(`/users/${this.$_myUserData.uid}/posts/${this.postId}/message`).push({
        text,
        type,
        time: this.$_getNowTime(this.ntpTimeDiff),
        ...data
      })
        .then(() => {
          this.delay = this.$_getNowTime(this.ntpTimeDiff) - sendTime;
          this.avrDelay = (this.avrDelay*this.countGetMessage + this.delay)/++this.countGetMessage;
        });
    },
    async updateSendInterval() {
      if (!this.sendIntervalId) {
        const ntpTimeDiff = await this.$_getNtpTimeDiff();
        this.ntpTimeDiff = ntpTimeDiff || 0;
        console.log('NTP time diff:', ntpTimeDiff);
        if (ntpTimeDiff !== undefined) {
          this.$_pushNotice('NTPサーバと時間を同期しました。', 'success');
        } else {
          this.$_pushNotice('NTPサーバに接続できませんでした。', 'error');
        }
        if (!this.streamStartTime) {
          this.streamStartTime = this.$_getNowTime(this.ntpTimeDiff);
        }
        this.stackedMidiSignals = [];
        this.sendIntervalId = setInterval(this.sendMidiSignals, this.intervalTime);
        this.$_pushNotice('配信を開始しました。', 'success');
      } else {
        clearInterval(this.sendIntervalId);
        this.sendIntervalId = null;
        this.resetDelay();
        this.$_pushNotice('配信を停止しました。', 'success');
      }
    },
    async closeStream(onlyOffline = false) {
      if (!onlyOffline && !await this.$refs.confirm.open('配信を終了しますか？', '終了', 'error')) {
        return;
      }
      if (this.sendIntervalId) {
        clearInterval(this.sendIntervalId);
        this.sendIntervalId = null;
      }
      if (!onlyOffline) {
        await firebase.database().ref(`/users/${this.$_myUserData.uid}/posts/${this.postId}/isClosed`).set(true);
        this.$_pushNotice('配信を終了しました。', 'success');
      } else {
        this.$_pushNotice('配信を終了しました。\n終了から60秒以内の場合、配信を再開することができます。', 'success');
      }
      firebase.database().goOffline();
      this.$store.dispatch('changeFirebaseState', false);
      window.removeEventListener('beforeunload', this.$_beforeunloadHandler);
      this.isClosedStream = true;
    },
    resetDelay() {
      this.delay = 0;
      this.countGetMessage = 0;
      this.avrDelay = 0;
    },
    sharePost() {
      const title = this.postDetails.title + ' - midies';
      const url = 'https://midies.web.app/post/' + this.postId;
      this.$refs.share.open(title, url);
    }
  }
};
</script>
